import { Injectable } from '@angular/core'
import { forkJoin, Observable, of } from 'rxjs'
import { first, mergeMap, map } from 'rxjs/operators'

import { MembershipPurchaseEligibility, PurchaseEligibilityCompilerInfo } from 'app/models/membership-purchase-eligibility'
import { MembershipDefinition } from 'app/models/membership-definition'
import { Item } from 'app/models/item'
import { Variation } from 'app/models/variation'
import { Mapper } from 'app/services/mapper.service'

import { Store } from './store'
import { ApiService } from './api.service'
import { MembershipPricingService } from './membership-pricing.service'
import { MembershipData } from 'app/obo/models/obo-purchase-input'
import { JobInfo } from 'app/models/job-info'

import * as polly from 'polly-js'
import * as wu from 'wu'

export interface MembershipDefinitionPricing {
  membership_definition_id?: string
  price: number
  customer_pays_fee: boolean
}

@Injectable({
  providedIn: 'root'
})
export class MembershipDefinitionService extends Store<MembershipDefinition> {

  public static FIND_PARAMS = {
    include_credential_definition_ids: 'true',
    include_tags: 'true',
    include_waivers: 'true',
    include_organization: 'true'
  }

  constructor(
    private api: ApiService,
    private membershipPricingService: MembershipPricingService
  ) {
    super(new MembershipDefinition({ }))
  }

  public findAllWithTags(bossOrganizationId: number): Observable<MembershipDefinition[]> {
    return this.api.get(`membership_definitions?boss_organization_id=${bossOrganizationId}
      &publish_status=published&include_tags=true&include_waivers=true&linkable=false`)
      .pipe(map((data) => {
        return Mapper.asArray<MembershipDefinition>(data.result, MembershipDefinition)
      }))
  }

  public findCurrent(id: string): void {
    this.find(id)
      .subscribe(
        (memdef: MembershipDefinition) => {
          this.subject.next(memdef)
          if (memdef.clare_item_uuid) this.getPrice(memdef)
        },
        (err: any) => {
          this.subject.error(err)
        }
      )
  }

  public find(id: string): Observable<MembershipDefinition> {
    return this.api.get(`membership_definitions/${id}`, { params: MembershipDefinitionService.FIND_PARAMS })
      .pipe(first())
      .pipe(map(data => {
        const memdef = Mapper.as<MembershipDefinition>(data.result, MembershipDefinition)
        memdef.metadata = data.metadata
        return memdef
      }))
  }

  public getPrice(memdef: MembershipDefinition): void {
    this.api.get(`finance/items/${memdef.clare_item_uuid}`)
      .subscribe(
        (data: any) => {
          const item = Mapper.as<Item>(data.result, Item)
          const matches = item.variations.filter((variation: Variation) => {
            return variation.id === item.default_variation_id
          })
          if (matches.length) {
            memdef.price_cents = matches[0].price_cents
          }
        },
        (err: any) => {
          this.subject.error(err)
        }
      )
  }

  public getParentMembershipDefinitions(memdef: MembershipDefinition): Observable<MembershipDefinition[]> {
    const stream: Observable<MembershipDefinition>[] = []
    memdef.parent_membership_definition_links.forEach(link => {
      stream.push(this.find(link.parent_membership_definition_id))
    })
    return stream.length ? forkJoin(stream) : of([])
  }

  /**
   * returns a map keyed by membership_definition_id with values as the price of the memdef for a given persona
   * also a 'total' property with a value of the total price will be included
   * @param memdef the main memdef being purchased - parent linked memberships will be looked up
   * @param personaId the persona for which the memdef(s) is being purchased for
   */
  public getBundledMembershipPricing(memdef: MembershipDefinition, personaId: number): Observable<any> {
    return this.getParentMembershipDefinitions(memdef)
      .pipe(mergeMap(parentMemdefs => {
        return this.getPricingForMemberships([...parentMemdefs, memdef], personaId)
          .pipe(map(prices => {
            return prices.reduce((accum, val) => {
              accum[val.membership_definition_id] = { price: val.price, customer_pays_fee: val.customer_pays_fee }
              accum.total += val.price
              return accum
            }, { total: 0 })
          }))
      }))
  }

  public getPricingForMemberships(
    memdefs: MembershipDefinition[],
    personaId: number
  ): Observable<MembershipDefinitionPricing[]> {
    const stream: Observable<MembershipDefinitionPricing>[] = []
    memdefs.forEach(md => stream.push(
      this.membershipPricingService.getPricing(md, personaId)
        .pipe(map(data => {
          return { membership_definition_id: md.id, price: data.price, customer_pays_fee: data.customer_pays_fee }
        }))
    ))
    return forkJoin(stream)
  }

  public getPurchaseEligibility(memDefId: string, bossOrganizationId: number, personaIds: number[])
    : Promise<MembershipPurchaseEligibility[]> {

    return this.api.post('membership_definitions/' + memDefId + '/eligibility', {
      persona_ids: personaIds,
      boss_organization_id: bossOrganizationId,
      requested_by: 'membership_ui'
    }).toPromise().then((data: any) => {
      return data.result as MembershipPurchaseEligibility[]
    })
  }

  /**
   * Creates a background job to check eligibility for a list of personas, and
   * waits for the job to finish before returning the data
  */
  public async getPurchaseEligibilityObo(memDefId: string, bossOrganizationId: number, personaIds: number[])
    : Promise<MembershipPurchaseEligibility[]> {

    return this.createEligibilityOboJob(memDefId, bossOrganizationId, personaIds)
      .then(jobInfo => this.getEligibilityOboWhenFinished(jobInfo))
  }

  /**
   * Retrieves eligibility for multiple sets of personas
   * Grouped by membership definition and org hierarchy
   * @param bossOrganizationId
   * @param membershipData
   */
  public async getBulkPurchaseEligibility(
    bossOrganizationId: number,
    membershipData: MembershipData[]
  ): Promise<MembershipPurchaseEligibility[]> {

    const promises = membershipData
      .map(membershipGroup => this.getPurchaseEligibilityObo(
        membershipGroup.membership_definition_id,
        bossOrganizationId,
        membershipGroup.persona_ids))

    const results = await Promise.all(promises)
    return results.flatMap(result => result)
  }

  public getMembershipDefinitions(membershipDefinitionIds: string[]): Observable<MembershipDefinition[]> {
    const membershipDefinitions$ = membershipDefinitionIds.map(this.find, this)
    return forkJoin(membershipDefinitions$)
  }

  /** Queues a background job to get the eligibility details for a list of personas. */
  private async createEligibilityOboJob(memDefId: string, bossOrganizationId: number, personaIds: number[])
    : Promise<PurchaseEligibilityCompilerInfo> {

    return this.api.post('membership_definitions/' + memDefId + '/eligibility_obo', {
      persona_ids: personaIds,
      boss_organization_id: bossOrganizationId
    }).toPromise().then((data: any) => {
      return data.result as PurchaseEligibilityCompilerInfo
    })
  }

  /**
   * Periodically checks the status of the eligibility background job and
   * returns the data once it is complete
   * */
  private async getEligibilityOboWhenFinished(jobInfo: JobInfo): Promise<MembershipPurchaseEligibility[]> {
    const self = this  // doing this because VS Code can't figure out the references with .bind(this)
    return polly()
      .handle((result: JobInfo) => result.type === 'PurchaseEligibilityCompilerInfo')
      .waitAndRetry(wu(this.delays()).take(100).toArray())
      .executeForPromise(async () => {
        const result = await self.getEligibilityOboJobInfo(jobInfo.id)
        // if it's not done, send a rejected promise and polly will retry
        if (result.items_completed !== result.items_total) { return Promise.reject(result) }
        return result.response_info
      })
  }

  /** Gets a generic job status */
  private getJobInfo(jobId: number): Promise<JobInfo> {
    return this.api.get('job_info/' + jobId).toPromise().then((data: any) => {
      return data.result as JobInfo
    })
  }

  /** Gets a verified eligibility_obo job status */
  private async getEligibilityOboJobInfo(jobId: number): Promise<PurchaseEligibilityCompilerInfo> {
    const result = await this.getJobInfo(jobId)
    if (result.type !== 'PurchaseEligibilityCompilerInfo') { return Promise.reject(result) }
    return result as PurchaseEligibilityCompilerInfo
  }

  /**
   * Generator function to create exponential-ish delays for polly, but it caps
   * the back-off delay at 4 seconds
   * */
  private * delays(): Generator<number> {
    const delays = [0.5, 1, 1, 2, 2, 2, 2]
    while (true) {
      yield (delays.shift() || 4) * 1000
    }
  }
}
